import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, SegmentedControl, Group, Text } from '@mantine/core';
import { IconTable, IconLayoutGrid } from '@tabler/icons-react';
import { LocalityDetailDialog, GenericEntityUpsertDialog } from '../dialogs';
import { DynamicTableView } from '../common/DynamicTableView';
import { type TableColumn } from '../common/GenericTable';
import { createLocalityManagementTableAdapter, type LocalitySearchFn } from '../common/tableAdapters';
import { entityService } from '../../services/entityServiceSingleton';
import { pushHashRoute, replaceHashRoute } from '../../utils/hashNavigationUtils';
import { UnifiedEntityScreen } from '../entities/UnifiedEntityScreen';
import { buildEntityScreenHash } from '../../utils/entityScreenRouteUtils';
import { ShadcnCardGrid } from '../entities/ShadcnCardGrid';
import { getEntityCardFieldsForEntity } from '../../registry/entityCardFields';

const ITEMS_PER_PAGE = 25;

export interface LocalityEntityManagementProps<T> {
    entityType: 'Building' | 'School' | 'Class' | 'ResponsibilityCenter';
    /** Hash base for list route (e.g. `#localities/school`, `#classes`). */
    hashBase: string;

    selectedEntityId?: string | null;
    selectedEntityViewMode?: 'view' | 'edit' | 'create' | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
    onBehalfOfKey?: string;

    /** Locality service list/search function. */
    search: LocalitySearchFn;
    /** Fetch the full entity for detail/edit deep links. */
    fetchById: (id: string) => Promise<T | null>;
    /** Columns for the table. */
    generateColumns: () => TableColumn<T>[];
    /** When true, disable row selection while deep-linked in edit mode (matches old Class behavior). */
    disableRowSelectWhenEditing?: boolean;
    /** When true, hide DynamicTableView's internal header (parent already renders its own). */
    hideTableHeader?: boolean;
    onUnsavedStateChange?: (state: { active: boolean; hasUnsavedChanges: boolean; mode: 'create' | 'edit'; entityType: string }) => void;
}

export function LocalityEntityManagement<T>(props: LocalityEntityManagementProps<T>) {
    const {
        entityType,
        hashBase,
        selectedEntityId,
        selectedEntityViewMode,
        deepLinkEntityType,
        authResolved = false,
        onBehalfOfKey,
        search,
        fetchById,
        generateColumns,
        disableRowSelectWhenEditing,
        hideTableHeader,
        onUnsavedStateChange
    } = props;

    const [selectedEntity, setSelectedEntity] = useState<T | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingEntity, setEditingEntity] = useState<T | null>(null);

    const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
    const [cloneEntity, setCloneEntity] = useState<T | null>(null);

    const [refreshSignal, setRefreshSignal] = useState(0);
    const newUiOpen = !!selectedEntityId
        && (selectedEntityViewMode === 'view' || selectedEntityViewMode === 'edit');

    const viewModeStorageKey = `hades_entity_list_view_mode_${entityType.toLowerCase()}`;
    const [listViewMode, setListViewMode] = useState<'table' | 'cards'>(() => {
        try {
            const stored = localStorage.getItem(viewModeStorageKey);
            if (stored === 'cards' || stored === 'table') return stored;
        } catch { /* noop */ }
        return 'table';
    });
    useEffect(() => {
        try { localStorage.setItem(viewModeStorageKey, listViewMode); } catch { /* noop */ }
    }, [viewModeStorageKey, listViewMode]);

    const closingDetailRef = useRef(false);
    /** Set when closing edit dialog so the "open edit from URL" effect skips one run and doesn't re-open. */
    const editDialogClosingRef = useRef(false);

    const tableAdapter = useMemo(
        () =>
            createLocalityManagementTableAdapter<T>({
                entityType,
                deleteEntityType: entityType,
                authResolved,
                itemsPerPage: ITEMS_PER_PAGE,
                entityService,
                search,
                getColumns: generateColumns
            }),
        [authResolved, entityService, entityType, generateColumns, search]
    );

    // Refresh table when onBehalfOfKey changes
    useEffect(() => {
        setRefreshSignal((s) => s + 1);
    }, [onBehalfOfKey]);

    const handleRefresh = useCallback(() => {
        setRefreshSignal((s) => s + 1);
    }, []);

    const getEntityId = (entity: any): string | undefined => {
        const id = entity?.Id ?? entity?.id;
        return typeof id === 'string' && id.trim() ? id : undefined;
    };

    const getCurrentHashSearch = useCallback((): string => {
        try {
            const h = typeof window !== 'undefined' ? (window.location.hash ?? '') : '';
            const idx = h.indexOf('?');
            return idx >= 0 ? h.slice(idx) : '';
        } catch {
            return '';
        }
    }, []);

    const buildNewUiHash = useCallback((mode: 'view' | 'edit', entityId: string) => {
        const currentSearch = getCurrentHashSearch();
        const next = buildEntityScreenHash(hashBase, mode, entityId);
        return currentSearch && !next.includes('?') ? `${next}${currentSearch}` : next;
    }, [getCurrentHashSearch, hashBase]);

    const handleEntitySelect = useCallback(async (entityOrId: T | string) => {
        if (closingDetailRef.current) return;
        const entityId = typeof entityOrId === 'string' ? entityOrId : getEntityId(entityOrId as any);
        if (!entityId) return;

        if (typeof entityOrId === 'object' && entityOrId) {
            setSelectedEntity(entityOrId as T);
        } else {
            try {
                const full = await fetchById(entityId);
                setSelectedEntity(full as T);
            } catch (err) {
                console.error(`Error loading ${entityType} for detail:`, err);
            }
        }

        pushHashRoute(buildNewUiHash('view', entityId));
    }, [buildNewUiHash, entityType, fetchById]);

    const handleEditFromTable = useCallback((entity: T) => {
        const id = getEntityId(entity as any);
        if (id) {
            pushHashRoute(buildNewUiHash('edit', id));
        }
        setEditingEntity(entity);
    }, [buildNewUiHash]);

    const handleCloseDetailDialog = useCallback(() => {
        closingDetailRef.current = true;
        setDetailDialogOpen(false);
        setSelectedEntity(null);
        setTimeout(() => {
            const currentSearch = getCurrentHashSearch();
            const next = currentSearch && !hashBase.includes('?') ? `${hashBase}${currentSearch}` : hashBase;
            // Deterministically leave /view/{id} so URL and table state can't drift.
            replaceHashRoute(next);
        }, 10);
    }, [hashBase, getCurrentHashSearch]);

    const lastDeepLinkRef = useRef<string | null>(null);
    useEffect(() => {
        if (!selectedEntityId) {
            lastDeepLinkRef.current = null;
            closingDetailRef.current = false;
            editDialogClosingRef.current = false;
            setEditDialogOpen(false);
            setEditingEntity(null);
            return;
        }
        if (!newUiOpen || !authResolved || !deepLinkEntityType) return;

        const norm = (s: string) => s.toLowerCase();
        if (norm(deepLinkEntityType) !== norm(entityType)) return;

        const isEditMode = selectedEntityViewMode === 'edit';
        const stub = { Id: selectedEntityId } as T;
        if (isEditMode) {
            if (!editingEntity || getEntityId(editingEntity as any) !== selectedEntityId) {
                setEditingEntity(stub);
            }
        } else if (!selectedEntity || getEntityId(selectedEntity as any) !== selectedEntityId) {
            setSelectedEntity(stub);
        }
    }, [authResolved, deepLinkEntityType, editingEntity, entityType, newUiOpen, selectedEntity, selectedEntityId, selectedEntityViewMode]);

    useEffect(() => {
        if (!newUiOpen) return;
        setDetailDialogOpen(false);
        setEditDialogOpen(false);
    }, [newUiOpen]);

    const onEntitySelect = disableRowSelectWhenEditing && selectedEntityViewMode === 'edit'
        ? undefined
        : handleEntitySelect;

    const localitySearchInAttributes = useMemo(
        () => ['DisplayName', 'Address', 'Id'] as const,
        []
    );

    const localityCardRenderer = useCallback((renderProps: {
        data: any[];
        isLoading: boolean;
        currentPage: number;
        totalResults: number;
        itemsPerPage: number;
        onPageChange: (page: number) => void;
        onRowClick?: (item: any) => void;
    }) => (
        <ShadcnCardGrid
            data={renderProps.data}
            entityType={entityType}
            isLoading={renderProps.isLoading}
            currentPage={renderProps.currentPage}
            totalResults={renderProps.totalResults}
            itemsPerPage={renderProps.itemsPerPage}
            onPageChange={renderProps.onPageChange}
            onEntitySelect={(entity) => renderProps.onRowClick?.(entity)}
            getCardFields={(entity) => getEntityCardFieldsForEntity(entityType, entity)}
        />
    ), [entityType]);

    const localityViewToggle = useMemo(() => (
        <SegmentedControl
            value={listViewMode}
            onChange={(val) => setListViewMode(val as 'table' | 'cards')}
            size="xs"
            data={[
                { label: <Group gap={4} wrap="nowrap"><IconTable size={14} /><Text size="xs">Table</Text></Group>, value: 'table' },
                { label: <Group gap={4} wrap="nowrap"><IconLayoutGrid size={14} /><Text size="xs">Cards</Text></Group>, value: 'cards' },
            ]}
        />
    ), [listViewMode]);

    return (
        <Box>
            {!newUiOpen && (
                <DynamicTableView
                    entityType={tableAdapter.entityType}
                    selectedEntityId={selectedEntityId}
                    deepLinkEntityType={deepLinkEntityType}
                    authResolved={authResolved}
                    hideHeader={hideTableHeader === true}
                    loadData={tableAdapter.loadData}
                    generateColumns={() => tableAdapter.getColumns()}
                    customHeaderContent={localityViewToggle}
                    customDataRenderer={listViewMode === 'cards' ? localityCardRenderer : undefined}
                    onEntitySelect={onEntitySelect}
                    onEditRowClick={handleEditFromTable}
                    onBehalfOfKey={onBehalfOfKey}
                    refreshSignal={refreshSignal}
                    deleteEntityById={tableAdapter.deleteById}
                    allowedSearchAttributeIds={[...localitySearchInAttributes]}
                />
            )}

            <LocalityDetailDialog
                entity={selectedEntity as any}
                entityType={entityType}
                open={detailDialogOpen && !newUiOpen}
                onClose={handleCloseDetailDialog}
                onEdit={(entity: any) => {
                    const id = getEntityId(entity);
                    if (id) {
                        pushHashRoute(buildNewUiHash('edit', id));
                    }
                    setDetailDialogOpen(false);
                    setEditingEntity(entity as T);
                    setEditDialogOpen(true);
                }}
                onOpenNewUi={(entity: any) => {
                    const id = getEntityId(entity);
                    if (!id) return;
                    pushHashRoute(buildNewUiHash('view', id));
                    setDetailDialogOpen(false);
                }}
                onClone={(entity: any) => {
                    pushHashRoute(`${hashBase}/create`);
                    setDetailDialogOpen(false);
                    setCloneEntity(entity as T);
                    setCloneDialogOpen(true);
                }}
                onDelete={async (entity: any) => {
                    const entityId = getEntityId(entity);
                    if (!entityId) return;
                    await entityService.deleteEntity(entityType, entityId);
                    handleCloseDetailDialog();
                    handleRefresh();
                }}
            />

            {editingEntity && (
                <GenericEntityUpsertDialog
                    key={`edit-${entityType}-${getEntityId(editingEntity as any) ?? 'unknown'}`}
                    open={editDialogOpen && !newUiOpen}
                    onClose={() => {
                        editDialogClosingRef.current = true;
                        setEditDialogOpen(false);
                        setEditingEntity(null);
                        pushHashRoute(hashBase);
                    }}
                    onSuccess={() => {
                        editDialogClosingRef.current = true;
                        setEditDialogOpen(false);
                        setEditingEntity(null);
                        handleRefresh();
                    }}
                    entityType={entityType}
                    entityId={getEntityId(editingEntity as any)}
                    existingEntity={editingEntity as any}
                    onOpenNewUi={() => {
                        const id = getEntityId(editingEntity as any);
                        if (!id) return;
                        pushHashRoute(buildNewUiHash('edit', id));
                        setEditDialogOpen(false);
                    }}
                />
            )}

            <UnifiedEntityScreen
                open={newUiOpen}
                entityType={entityType}
                entityId={selectedEntityId ?? ''}
                mode={selectedEntityViewMode === 'edit' ? 'edit' : 'view'}
                existingEntity={(editingEntity ?? selectedEntity) as any}
                onClose={() => {
                    const currentSearch = getCurrentHashSearch();
                    const next = currentSearch && !hashBase.includes('?') ? `${hashBase}${currentSearch}` : hashBase;
                    replaceHashRoute(next);
                }}
                onModeChange={(mode) => {
                    if (!selectedEntityId) return;
                    replaceHashRoute(buildNewUiHash(mode, selectedEntityId));
                }}
                onSuccess={() => {
                    const currentSearch = getCurrentHashSearch();
                    const next = currentSearch && !hashBase.includes('?') ? `${hashBase}${currentSearch}` : hashBase;
                    replaceHashRoute(next);
                    handleRefresh();
                }}
                onUnsavedStateChange={onUnsavedStateChange}
            />

            {cloneDialogOpen && cloneEntity && (
                <GenericEntityUpsertDialog
                    key={`clone-${entityType}-${getEntityId(cloneEntity as any) ?? 'new'}`}
                    open={cloneDialogOpen}
                    onClose={() => {
                        setCloneDialogOpen(false);
                        setCloneEntity(null);
                    }}
                    onSuccess={() => {
                        setCloneDialogOpen(false);
                        setCloneEntity(null);
                        handleRefresh();
                    }}
                    entityType={entityType}
                    existingEntity={{
                        ...(cloneEntity as any),
                        Id: undefined,
                        id: undefined,
                        DisplayName: (cloneEntity as any)?.DisplayName ? `${(cloneEntity as any).DisplayName} (Copy)` : undefined
                    }}
                />
            )}
        </Box>
    );
}

